import { type IMediator } from '#/core/mediator';

import { SystemError } from '#/shared/exceptions/system-error';
import { safeJsonParse } from '#/shared/utils/safe-json-parse';

import { SERVICES_TYPES } from '#/services-types';

import { type ILogService } from '../log';

import {
  type IMessageListenerService,
  type MessageListenerHandlerKey,
  type MessagesChannel,
} from './message-listener.types';

export class MessageListenerService implements IMessageListenerService {
  #serviceName = 'MessageListenerService';

  get #logService() {
    return this._mediator.get<ILogService>(SERVICES_TYPES.LogService);
  }

  readonly #handlers = new Map<MessagesChannel, Map<MessageListenerHandlerKey, (messagePayload: unknown) => void>>();

  #payloadParser = (payload: string) => {
    const TIME_KEYS = ['time'];
    const MS_THRESHOLD = 1e12;

    return JSON.parse(payload, (key, value) => {
      if (TIME_KEYS.includes(key) && typeof value === 'number') {
        const second = value > MS_THRESHOLD ? Math.floor(value / 1000) : value;

        return { raw: value, second };
      }

      return value;
    });
  };

  #globalHandler = (event: MessageEvent<unknown>) => {
    if (this.#handlers.size === 0) return;

    const messagePayload = safeJsonParse(event.data, { parse: this.#payloadParser });

    if (messagePayload && typeof messagePayload === 'object' && 'type' in messagePayload) {
      const messageType = messagePayload.type as MessagesChannel;

      const messageHandlers = this.#handlers.get(messageType);

      if (!messageHandlers) return;

      for (const [, handler] of messageHandlers) {
        handler(messagePayload);
      }

      return;
    }
  };

  constructor(private readonly _mediator: IMediator) {
    this.#logService.info(this.#serviceName, 'Instance was created.');

    window.addEventListener('message', this.#globalHandler);
  }

  setHandler(
    channel: MessagesChannel,
    handlerKey: MessageListenerHandlerKey,
    handler: (messagePayload: unknown) => void
  ) {
    const channelHandlers = this.#handlers.get(channel);

    if (!channelHandlers) {
      this.#handlers.set(channel, new Map([[handlerKey, handler]]));
    } else {
      const handlerByKey = channelHandlers.get(handlerKey);

      if (!handlerByKey) {
        channelHandlers.set(handlerKey, handler);
      } else {
        this.#logService.error(
          this.#serviceName,
          new SystemError(`Handler with key '${handlerKey}' for channel '${channel}' already exists.`)
        );
      }
    }
  }
}
